"use client";

import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Skeleton from "./Skeleton";

export const BodyTableType = {
  Text: "text",
  HTML: "html",
  Estado: "status",
};

const formatContent = (type, content) => {
  try {
    switch (type) {
      case "status":
        return content == true ? "Activo" : "Inactivo";
      default:
        return content;
    }
  } catch {
    return content;
  }
};

/**
 * TableThree
 * ----------------------------------------------------------------------------
 *  Esta es la tabla "estándar" de la app — la usan muchísimas pantallas
 *  (consultas, certificados, trámites, etc). Por eso vale la pena que
 *  esté bien afinada para tema oscuro.
 *
 *  Cambios respecto a la versión anterior:
 *    - El contenedor usa tokens semánticos (`surface`, `border-default`)
 *      en vez de `bg-white` y `border-stroke` cocinados.
 *    - Las celdas y bordes internos usan `border-default` y se ven
 *      correctamente en oscuro (antes bordeaban con #eee fijo).
 *    - El header de tabla (`<thead>`) usa `surface-muted` para
 *      diferenciarse sutilmente del cuerpo, en ambos temas.
 *    - Los botones de paginación tienen variantes oscuras explícitas;
 *      el botón ACTIVO se distingue con `bg-primary text-white`.
 *    - El select de "Mostrar 10/25/50/100" hereda los colores correctos
 *      gracias a DaisyUI + data-theme.
 * ----------------------------------------------------------------------------
 */
const TableThree = ({
  data,
  dataCount = 0,
  page,
  numbered = false,
  isLoading = false,
  onChange,
}) => {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const body = data?.body ?? [];
  const sortedData = [...body];

  if (sortKey) {
    sortedData.sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  useEffect(() => {
    let totPages = Math.ceil(dataCount / itemsPerPage);
    setPages(totPages);
  }, [itemsPerPage, dataCount]);

  // Clases reutilizables para los botones de paginación.
  // Las saco a constantes para que el JSX quede legible y para que
  // si mañana cambia un color, lo cambie en UN solo lugar.
  const pageBtnBase =
    "btn join-item border border-border-default dark:border-dark-border-default " +
    "bg-surface dark:bg-dark-surface " +
    "text-text-default dark:text-dark-text-default " +
    "hover:bg-surface-muted dark:hover:bg-dark-surface-muted";

  const pageBtnActive =
    "btn join-item border-none bg-primary text-white hover:bg-primary/90";

  const renderPaginationButtons = useCallback(
    (pages) => {
      const buttons = [];
      const maxButtons = 10;
      let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let endPage = Math.min(pages, startPage + maxButtons - 1);

      if (endPage - startPage < maxButtons - 1) {
        startPage = Math.max(1, endPage - maxButtons + 1);
      }

      if (startPage > 1) {
        buttons.push(
          <button
            key="prevdots"
            className={pageBtnBase}
            onClick={() => {
              const newPage =
                currentPage > maxButtons ? currentPage - maxButtons : 1;
              setCurrentPage(newPage);
              onChange(newPage, itemsPerPage);
            }}
          >
            {"..."}
          </button>,
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            className={currentPage === i ? pageBtnActive : pageBtnBase}
            onClick={() => {
              setCurrentPage(i);
              onChange(i, itemsPerPage);
            }}
          >
            {i}
          </button>,
        );
      }

      if (endPage < pages) {
        buttons.push(
          <button
            key="nextdots"
            className={pageBtnBase}
            onClick={() => {
              const newPage =
                currentPage + maxButtons < pages
                  ? currentPage + maxButtons
                  : pages;
              setCurrentPage(newPage);
              onChange(newPage, itemsPerPage);
            }}
          >
            {"..."}
          </button>,
        );
      }

      return buttons;
    },
    [currentPage, itemsPerPage, onChange, pageBtnBase],
  );

  const changeSelectedPagination = (data) => {
    setItemsPerPage(data);
    const newPages = Math.ceil(dataCount / data);
    setPages(newPages);

    const newPage = Math.min(currentPage, newPages);
    setCurrentPage(newPage);
    onChange(newPage, data);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  return isLoading ? (
    <Skeleton columns={data.head} rows={10} />
  ) : (
    <>
      {/* Contenedor de la tabla. Antes: bg-white + border-stroke + dark:border-strokedark
         Ahora: tokens semánticos. La sombra se atenúa en oscuro porque
         sobre fondo oscuro las sombras grandes se notan como "manchas". */}
      <div
        className="rounded-md border bg-surface dark:bg-dark-surface
                   border-border-default dark:border-dark-border-default
                   px-5 pb-2.5 pt-6 shadow-default dark:shadow-none
                   sm:px-7.5 xl:pb-1"
      >
        <div className="max-w-full overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              {/* La cabecera usa una superficie ligeramente distinta para
                 separar visualmente del cuerpo. */}
              <tr className="bg-surface-muted dark:bg-dark-surface-muted text-left">
                {numbered ? (
                  <th className="min-w-[24px] px-4 py-4 font-medium text-text-default dark:text-dark-text-default xl:pl-11">
                    #
                  </th>
                ) : null}
                {data.head.map((head, index) => (
                  <th
                    key={index}
                    className={`cursor-pointer px-4 py-4 font-medium text-text-default dark:text-dark-text-default ${head.style ?? ""}`}
                    onClick={() => handleSort(head.key)}
                  >
                    {head.title}
                    {head.sort && sortKey === head.key && (
                      sortDirection === "asc" ? " ↑" : " ↓"
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(!sortedData || sortedData.length === 0 || dataCount === 0) ? (
                <tr>
                  <td
                    colSpan={numbered ? data.head.length + 1 : data.head.length}
                    className="border-b border-border-default dark:border-dark-border-default px-4 py-5 text-center"
                  >
                    <p className="text-text-muted dark:text-dark-text-muted">
                      No se encontraron resultados
                    </p>
                  </td>
                </tr>
              ) : (
                sortedData
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((value, index) => (
                    // Filas con efecto hover sutil — útil cuando hay muchas filas.
                    <tr
                      key={index}
                      id={value["id"]}
                      className="hover:bg-surface-muted dark:hover:bg-dark-surface-muted transition-colors"
                    >
                      {numbered ? (
                        <td className="border-b border-border-default dark:border-dark-border-default px-4 py-5 pl-9">
                          <p className="text-text-default dark:text-dark-text-default">
                            {(currentPage - 1) * itemsPerPage + (index + 1)}
                          </p>
                        </td>
                      ) : null}

                      {data.head.map((item, index) => (
                        <td
                          key={index}
                          className="border-b border-border-default dark:border-dark-border-default px-4 py-5"
                        >
                          <p
                            className={`text-text-default dark:text-dark-text-default ${item.styleContent ?? ""} ${item.type === "status" ? (value[item.key] == true ? "bg-success/10 text-success" : "bg-danger/10 text-danger") : ""}`}
                          >
                            {item.formatter != null
                              ? item.formatter(value[item.key])
                              : formatContent(
                                  item.type,
                                  item.type == "html"
                                    ? item.extend?.(value)
                                    : value[item.key],
                                )}
                          </p>
                        </td>
                      ))}
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Barra inferior: selector de filas + paginación.
         Le ponemos un margen superior para que respire la tabla. */}
      <div className="mt-4 flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Selector de cantidad. El select hereda los colores de DaisyUI
           gracias a data-theme; le añadimos un wrapper con tokens. */}
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
          <label className="text-sm font-medium text-text-muted dark:text-dark-text-muted">
            Mostrar
          </label>
          <select
            className="select select-bordered w-full sm:w-auto
                       bg-surface dark:bg-dark-surface
                       text-text-default dark:text-dark-text-default
                       border-border-default dark:border-dark-border-default"
            value={itemsPerPage}
            onChange={(a) => changeSelectedPagination(parseInt(a.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Controles de paginación */}
        <div className="join flex flex-wrap justify-center gap-0 sm:justify-end">
          <button
            className={pageBtnBase}
            onClick={() => {
              setCurrentPage(1);
              onChange(1, itemsPerPage);
            }}
          >
            «
          </button>
          <button
            disabled={currentPage === 1}
            className={`${pageBtnBase} disabled:opacity-50`}
            onClick={() => {
              const newPage = currentPage > 1 ? currentPage - 1 : 1;
              setCurrentPage(newPage);
              onChange(newPage, itemsPerPage);
            }}
          >
            Anterior
          </button>
          {renderPaginationButtons(pages)}
          <button
            disabled={currentPage === pages || pages === 0}
            className={`${pageBtnBase} disabled:opacity-50`}
            onClick={() => {
              const newPage = pages > currentPage ? currentPage + 1 : pages;
              setCurrentPage(newPage);
              onChange(newPage, itemsPerPage);
            }}
          >
            Siguiente
          </button>
          <button
            className={pageBtnBase}
            onClick={() => {
              setCurrentPage(pages);
              onChange(pages, itemsPerPage);
            }}
          >
            »
          </button>
        </div>
      </div>
    </>
  );
};

export default TableThree;

TableThree.propTypes = {
  data: PropTypes.shape({
    head: PropTypes.array.isRequired,
    body: PropTypes.array.isRequired,
  }).isRequired,
  dataCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  numbered: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
